/**
 * Custody when a technician's work (re)starts — the rule behind the
 * CUSTODY_ACCEPT success effect of Start and Resume.
 *
 * Pure (no I/O, no React, no Supabase client) so every surface — and the
 * real-stack workflow gate — runs the exact same rule.
 */
import type { CustodyEventKind, CustodyStatus } from '@/lib/tickets/lifecycle/types';

/**
 * Decides whether a successful timer start/resume should also auto-fire
 * a custody transition.
 *
 * The product invariant: when a ticket has been kicked back from FOH
 * (custody = `pending_tech_accept`) and the technician resumes work by
 * starting/resuming the timer, the custody state must move to
 * `with_tech` so:
 *   - the FOH Queue stops including the ticket
 *   - the destructive "BOH · Pending Tech" pill on the ticket card
 *     transitions back to the calm "BOH · With Tech" chip
 *
 * Core timer workflow gate: the SAME invariant from the other side. A Stage D
 * advisor/customer Pause that was the last live BOH work hands the ticket to
 * the FOH queue (`pending_advisor_accept`). If the technician starts/resumes
 * work before an advisor accepted it, the ticket is BOH work again: it takes
 * it back (`reject_back` → `with_tech`). Leaving it pending contradicted live
 * work — the advisor queue showed a ticket a technician was working, and the
 * final Stop then skipped its handoff and "work finished" notification
 * because custody was no longer `with_tech`.
 *
 * `with_advisor` is left alone: an advisor OWNS it and returns it with
 * `kick_to_boh` (then this rule's `boh_accept` applies).
 *
 * Returns the eventKind when the state warrants it, otherwise null.
 */
export function nextCustodyEventOnTimerStart(
  custody: { custody_status: CustodyStatus } | null | undefined,
): CustodyEventKind | null {
  if (!custody) return null;
  if (custody.custody_status === 'pending_tech_accept') return 'boh_accept';
  if (custody.custody_status === 'pending_advisor_accept') return 'reject_back';
  return null;
}
